<script setup lang="ts">
import type { HTMLAttributes, Component } from 'vue'
import { ref, computed, watch } from 'vue'
import { cn } from '@/lib/utils'
import BubbleFrame from './BubbleFrame.vue'

interface Props {
  class?: HTMLAttributes['class']
  /** Whether the bubble is expanded */
  expanded?: boolean
  /** Width when expanded (in pixels) */
  expandedWidth?: number
  /** Height when collapsed (for mini player) */
  collapsedHeight?: number
  /** Size when collapsed */
  collapsedSize?: number
  /** Unique ID for SVG defs */
  id?: string
  /** Whether to show the diamond ornament */
  showDiamond?: boolean
  /** Transform origin for animations (e.g., 'top left', 'top right') */
  origin?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
}

const props = withDefaults(defineProps<Props>(), {
  expanded: false,
  expandedWidth: 320,
  collapsedSize: 48,
  collapsedHeight: 48,
  id: 'bubble',
  showDiamond: true,
  origin: 'top-left'
})

const emit = defineEmits<{
  'update:expanded': [value: boolean]
}>()

// Compute current dimensions based on expanded state
const currentWidth = computed(() => 
  props.expanded ? props.expandedWidth : props.collapsedSize
)

const currentHeight = computed(() => 
  props.expanded ? 'auto' : `${props.collapsedHeight}px`
)

// Map origin prop to CSS transform-origin
const transformOrigin = computed(() => {
  const map = {
    'top-left': 'top left',
    'top-right': 'top right',
    'bottom-left': 'bottom left',
    'bottom-right': 'bottom right'
  }
  return map[props.origin]
})
</script>

<template>
  <div
    :class="cn(
      'bubble-wrapper relative',
      expanded ? 'z-[60]' : 'z-0',
      props.class
    )"
    :style="{
      width: `${currentWidth}px`,
      maxWidth: 'calc(100vw - 32px)',
      height: currentHeight,
      minHeight: `${collapsedHeight}px`,
      transformOrigin: transformOrigin
    }"
  >
    <!-- SVG Frame that morphs with size -->
    <BubbleFrame :id="id" :show-diamond="showDiamond && expanded" />
    
    <!-- Content wrapper with overflow hidden -->
    <div class="bubble-content relative z-10 flex flex-col h-full overflow-hidden">
      <slot :expanded="expanded" />
    </div>
  </div>
</template>

<style scoped>
.bubble-wrapper {
  filter: drop-shadow(0 10px 25px rgba(0, 0, 0, 0.3))
          drop-shadow(0 0 20px rgba(180, 140, 70, 0.1));
  /* Use same timing for all properties */
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.bubble-wrapper:hover {
  filter: drop-shadow(0 15px 35px rgba(0, 0, 0, 0.4))
          drop-shadow(0 0 25px rgba(180, 140, 70, 0.15));
}

.bubble-content {
  /* Content fades in slightly after the container starts expanding */
  transition: opacity 0.15s ease;
}
</style>
